/**
 * Tip — ajuda contextual atrás de um ícone.
 *
 * Por que não `title=` nativo (que o projeto usa em 131 lugares): o atributo
 * não aparece em touch, e este app é PWA usado no celular dentro do galpão —
 * ou seja, a explicação some justamente pra quem está com as mãos no
 * equipamento. Também não segue o tema escuro e tem o delay do sistema.
 *
 * Abre por hover no desktop E por toque no mobile, fecha com Esc, clique
 * fora ou blur.
 *
 * POR QUE O BALÃO VAI PRO <body> POR PORTAL, e não fica ao lado do ícone:
 *
 *   O balão é `position: absolute` dentro do gatilho. Absoluto resolve a
 *   POSIÇÃO, mas não escapa do RECORTE: qualquer ancestral com `overflow`
 *   diferente de `visible` corta o que passa da borda dele — e `z-index` não
 *   tem nada a ver com isso, recorte não é empilhamento. O corpo do `Modal`
 *   é `overflow-y-auto`, então um Tip na primeira linha do modal abria pra
 *   cima, pra fora do corpo rolável, e chegava cortado no cabeçalho. Texto
 *   curto passava despercebido; texto longo sumia pela metade.
 *
 *   Com portal pro `<body>` o balão não tem mais ancestral que o recorte.
 *   O preço é que ele deixa de acompanhar o gatilho sozinho: a posição passa
 *   a ser calculada em coordenadas de viewport (`position: fixed`) e
 *   recalculada em scroll e resize enquanto estiver aberto.
 *
 * E ele VIRA PRA BAIXO quando não cabe em cima, em vez de ser cortado — é a
 * diferença entre a ajuda aparecer e a ajuda existir só no código.
 */

import {
  useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";
import clsx from "clsx";

/** Respiro entre o ícone e o balão, e entre o balão e a borda da tela. */
const MARGEM = 8;
/** Largura de referência antes da primeira medição. */
const LARGURA_PADRAO = 240;

interface TipProps {
  children: ReactNode;
  /** Rótulo acessível do botão — diga o que a ajuda explica. */
  label?: string;
  className?: string;
}

interface Posicao {
  top: number;
  left: number;
}

export function Tip({ children, label = "Ajuda", className }: TipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Posicao | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const balaoRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  // Posição em coordenadas de viewport. `useLayoutEffect` porque o cálculo
  // precisa acontecer entre o balão existir no DOM (pra ter altura medida) e
  // o navegador pintar — em `useEffect` o balão aparece um quadro no canto
  // superior esquerdo antes de pular pro lugar.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }

    function calcular() {
      const gatilho = wrapRef.current?.getBoundingClientRect();
      if (!gatilho) return;
      const balao = balaoRef.current?.getBoundingClientRect();
      const altura = balao?.height ?? 0;
      const largura = balao?.width ?? LARGURA_PADRAO;

      // Em cima por padrão; embaixo quando não cabe. Cortar não é opção.
      const cabeEmCima = gatilho.top >= altura + MARGEM;
      const top = cabeEmCima
        ? gatilho.top - altura - MARGEM
        : gatilho.bottom + MARGEM;

      // Centralizado no ícone, grampeado na viewport: perto da borda direita
      // o balão centralizado sairia da tela.
      const meio = gatilho.left + gatilho.width / 2;
      const left = Math.min(
        Math.max(MARGEM, meio - largura / 2),
        Math.max(MARGEM, window.innerWidth - largura - MARGEM),
      );

      setPos({ top, left });
    }

    calcular();
    // `true` = fase de captura: pega o scroll de QUALQUER container, não só
    // o da janela. O corpo do modal é um deles.
    window.addEventListener("scroll", calcular, true);
    window.addEventListener("resize", calcular);
    return () => {
      window.removeEventListener("scroll", calcular, true);
      window.removeEventListener("resize", calcular);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent) {
      const alvo = e.target as Node;
      // O balão não está mais dentro do wrapper — sem checá-lo também, um
      // clique no próprio texto da ajuda fecharia a ajuda.
      if (wrapRef.current?.contains(alvo)) return;
      if (balaoRef.current?.contains(alvo)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={clsx("relative inline-flex align-middle", className)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        // Hover só pra mouse — em touch o pointerenter dispara junto do tap
        // e o toggle do onClick fecharia na mesma interação.
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setOpen(false);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="rounded-full text-slate-400 transition-colors hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-slate-300"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>

      {open && createPortal(
        <span
          ref={balaoRef}
          id={id}
          role="tooltip"
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            // Antes da primeira medição o balão precisa existir (pra ter
            // altura) sem ser visto no canto da tela.
            visibility: pos ? "visible" : "hidden",
          }}
          className={clsx(
            "fixed z-[60] w-[min(15rem,calc(100vw-1rem))]",
            // Ajuda muito longa rola dentro do balão em vez de estourar a
            // altura da tela — que é o mesmo corte, só que na viewport.
            "max-h-[60vh] overflow-y-auto overscroll-contain",
            "rounded-lg border border-surface-200 bg-white p-2.5 text-xs",
            "font-normal normal-case leading-relaxed tracking-normal",
            "text-slate-600 shadow-lg",
            "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
          )}
        >
          {children}
        </span>,
        document.body,
      )}
    </span>
  );
}
